// /cabinet/edu-consult?child=<uuid> — заказ консультации и свой бриф.
//
// Пока брифа нет — форма заказа со вторым согласием. Как только он есть —
// показываем его целиком: семья должна видеть, что именно ушло консультанту.

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { loginRedirect } from "@/lib/return-to";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { getEduBrief, orderEduConsultation } from "@/lib/edu-brief.functions";
import { getEduProfile } from "@/lib/edu-profile.functions";
import { EduConsultationOrder, type OrderValues } from "@/components/edu/EduConsultationOrder";
import { getEduAvailability } from "@/lib/edu-availability";
import {
  DIET_CHOICES,
  HEALTH_CHOICES,
  HOUSING_CHOICES,
  LIMIT_NONE_ID,
  labelOf,
  toId,
} from "@/edu/answer-values";
import { EduBriefView } from "@/components/edu/EduBriefView";
import { EduClosedNotice } from "@/components/edu/EduClosedNotice";

export const Route = createFileRoute("/cabinet_/edu-consult")({
  validateSearch: (search: Record<string, unknown>): { child?: string } => ({
    child: typeof search.child === "string" ? search.child : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Консультация — образование за рубежом" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EduConsultPage,
});

function EduConsultPage() {
  const { child: childId } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const fetchBrief = useServerFn(getEduBrief);
  const fetchProfile = useServerFn(getEduProfile);
  const order = useServerFn(orderEduConsultation);
  const checkOpen = useServerFn(getEduAvailability);

  const [authChecked, setAuthChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        // Язык и путь возврата едут на вход: после письма человек вернётся сюда.
        navigate(loginRedirect());
        return;
      }
      setAuthChecked(true);
    });
  }, [navigate]);

  // Модуль закрыт, пока справочник стран не опубликован: см. edu-availability.
  const moduleOpen = useQuery({
    queryKey: ["edu-open"],
    enabled: authChecked,
    queryFn: () => checkOpen({}),
  });
  const isOpen = moduleOpen.data?.open === true;

  const brief = useQuery({
    queryKey: ["edu-brief", childId],
    enabled: Boolean(authChecked && isOpen && childId),
    queryFn: () => fetchBrief({ data: { childId: childId as string } }),
  });

  const profile = useQuery({
    queryKey: ["edu-profile", childId],
    enabled: Boolean(authChecked && isOpen && childId),
    queryFn: () => fetchProfile({ data: { childId: childId as string } }),
  });

  const submit = useMutation({
    mutationFn: (v: OrderValues) =>
      order({
        data: {
          childId: childId as string,
          parentName: v.parentName,
          phone: v.phone || null,
          preferredTime: v.preferredTime || null,
          healthDetail: v.healthDetail || null,
          dietDetail: v.dietDetail || null,
          housingDetail: v.housingDetail || null,
          consentGranted: v.consentGranted,
        },
      }),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["edu-brief", childId] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
  });

  if (!childId) {
    return (
      <div className="mx-auto max-w-2xl p-6 text-sm text-muted-foreground">
        Не указан ребёнок. Откройте страницу из кабинета.
      </div>
    );
  }
  if (!authChecked || moduleOpen.isLoading) {
    return <div className="mx-auto max-w-2xl p-6 text-sm text-muted-foreground">Загрузка…</div>;
  }
  if (!isOpen) return <EduClosedNotice />;
  if (brief.isLoading) {
    return <div className="mx-auto max-w-2xl p-6 text-sm text-muted-foreground">Загрузка…</div>;
  }

  if (brief.data?.brief) {
    return (
      <EduBriefView
        payload={brief.data.brief.payload}
        meta={{
          createdAt: brief.data.brief.created_at,
          consentVersion: brief.data.brief.consent_version,
        }}
      />
    );
  }

  const answers = (profile.data?.profile?.answers ?? {}) as Record<string, unknown>;
  // Ответ — это id, а не слово «нет»: сравнение с подписью осталось здесь после
  // перехода на идентификаторы и давало обратный результат — семья, ответившая
  // «нет», видела просьбу уточнить ограничение. Показываем подписи, считаем по
  // id (тот же приём, что в profile-to-family.ts).
  const notNone = (table: readonly { id: string; label: string }[], v: unknown) => {
    const id = toId(table, v);
    return id && id !== LIMIT_NONE_ID ? labelOf(table, id) : null;
  };
  const flags = {
    health: notNone(HEALTH_CHOICES, answers.q_health),
    diet: (Array.isArray(answers.q_diet) ? answers.q_diet : [])
      .map((d) => notNone(DIET_CHOICES, d))
      .filter((d): d is string => Boolean(d)),
    housing: notNone(HOUSING_CHOICES, answers.q_housing),
  };

  return (
    <div className="min-h-screen bg-background px-4 py-8">
      <EduConsultationOrder
        flags={flags}
        saving={submit.isPending}
        error={error}
        onSubmit={(v) => submit.mutate(v)}
      />
    </div>
  );
}
